// Phase 9 R2 — Filters bar (search/status/stock/category/supplier/sort).
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  StatusFilter,
  StockFilter,
  SortColumn,
  SortDir,
} from '@/hooks/inventory/useInventoryItems';
import { ArrowDownUp } from 'lucide-react';

export interface InventoryFiltersBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  status: StatusFilter;
  onStatusChange: (v: StatusFilter) => void;
  stock: StockFilter;
  onStockChange: (v: StockFilter) => void;
  category: string | null;
  onCategoryChange: (v: string | null) => void;
  supplier: string | null;
  onSupplierChange: (v: string | null) => void;
  sortColumn: SortColumn;
  onSortColumnChange: (v: SortColumn) => void;
  sortDir: SortDir;
  onSortDirChange: (v: SortDir) => void;
  categories: string[];
  suppliers: string[];
}

export function InventoryFiltersBar(props: InventoryFiltersBarProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
      <Input
        placeholder="Search part #, name, brand, supplier…"
        value={props.search}
        onChange={(e) => props.onSearchChange(e.target.value)}
        className="md:col-span-2"
      />

      <Select value={props.status} onValueChange={(v) => props.onStatusChange(v as StatusFilter)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active only</SelectItem>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="inactive">Inactive only</SelectItem>
        </SelectContent>
      </Select>

      <Select value={props.stock} onValueChange={(v) => props.onStockChange(v as StockFilter)}>
        <SelectTrigger><SelectValue placeholder="Stock" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All stock</SelectItem>
          <SelectItem value="low">Low stock</SelectItem>
          <SelectItem value="out">Out of stock</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={props.category ?? '__all__'}
        onValueChange={(v) => props.onCategoryChange(v === '__all__' ? null : v)}
      >
        <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All categories</SelectItem>
          {props.categories.map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={props.supplier ?? '__all__'}
        onValueChange={(v) => props.onSupplierChange(v === '__all__' ? null : v)}
      >
        <SelectTrigger><SelectValue placeholder="Supplier" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All suppliers</SelectItem>
          {props.suppliers.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="md:col-span-6 flex items-center gap-2">
        <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
        <Select value={props.sortColumn} onValueChange={(v) => props.onSortColumnChange(v as SortColumn)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="part_number">Part #</SelectItem>
            <SelectItem value="category">Category</SelectItem>
            <SelectItem value="quantity_available">Available</SelectItem>
            <SelectItem value="reorder_point">Reorder Point</SelectItem>
            <SelectItem value="updated_at">Updated</SelectItem>
          </SelectContent>
        </Select>
        <Select value={props.sortDir} onValueChange={(v) => props.onSortDirChange(v as SortDir)}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">Ascending</SelectItem>
            <SelectItem value="desc">Descending</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
